import { useQuery } from "@tanstack/react-query";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import { t } from "ttag";

import { usePatientQuery, usePortalUser } from "@/app/hooks";
import { ROUTES } from "@/app/router";
import { usePortalUserStore } from "@/app/stores";
import { consultationsQueriesStore } from "@/features/consultations/api";
import { ScheduleContextProvider } from "@/features/consultations/contexts";
import {
  APPOINTMENT_TYPE,
  appointmentSchema,
} from "@/features/consultations/models";
import {
  ScheduleAvailableDaysCard,
  ScheduleAvailableTimeSlotCard,
  ScheduleSubmit,
} from "@/features/consultations/sections";
import { LogoLoader } from "@/shared/components";
import { useSettingsStore } from "@/shared/stores";
import { Card } from "@/shared/ui";
import { ScheduleJourneySupportHeader } from "../sections";

export const ScheduleJourneySupport = () => {
  const { data: location } = usePatientQuery((patient) => patient.location);
  const { data: portalUser, isFetching: isFetchingPortalUser } =
    usePortalUser();

  const language = useSettingsStore((state) => state.language);
  const appointmentType = usePortalUserStore((state) =>
    state.portalUser?.journeySupport?.firstTime
      ? APPOINTMENT_TYPE.JOURNEY_SUPPORT_INITIAL
      : APPOINTMENT_TYPE.JOURNEY_SUPPORT_FOLLOW_UP,
  );

  const { appointmentId } = useParams();

  const [searchParams] = useSearchParams();
  const subscriptionIdParam = searchParams.get("subscriptionId");
  const parsedAppointmentType = appointmentSchema.shape.type.safeParse(
    searchParams.get("type"),
  ).data;

  const rescheduleParams =
    parsedAppointmentType || subscriptionIdParam
      ? {
          appointmentType:
            parsedAppointmentType ?? APPOINTMENT_TYPE.JOURNEY_SUPPORT,
          subscriptionId: subscriptionIdParam
            ? Number(subscriptionIdParam)
            : undefined,
        }
      : undefined;

  const isReschedule = Boolean(appointmentId);

  const {
    data: appointmentToReschedule,
    isLoading: isLoadingAppointmentToReschedule,
    isSuccess: isSuccessAppointmentToReschedule,
  } = useQuery(
    consultationsQueriesStore.consultations.appointments._ctx.detail(
      appointmentId,
      rescheduleParams,
    ),
  );

  if (
    !isFetchingPortalUser &&
    !portalUser?.journeySupport?.canSchedule &&
    !isReschedule
  ) {
    return <Navigate to={ROUTES.COACHING.OVERVIEW} />;
  }

  return (
    <>
      <ScheduleJourneySupportHeader isReschedule={isReschedule} />

      <LogoLoader
        show={isFetchingPortalUser || isLoadingAppointmentToReschedule}
        withOverlay
      />

      {(isSuccessAppointmentToReschedule || !appointmentId) && (
        <ScheduleContextProvider
          appointmentToReschedule={appointmentToReschedule}
          defaultLanguage={language}
          appointmentType={appointmentType}
          state={location?.state}
          subscriptionId={
            subscriptionIdParam ? Number(subscriptionIdParam) : undefined
          }
        >
          <div className="grid gap-6 md:grid-cols-2 md:grid-rows-[auto_32.5rem]">
            <ScheduleAvailableDaysCard
              title={`1. ${t`Choose an available day`}`}
            />

            <ScheduleAvailableTimeSlotCard titleStep={2}>
              <Card.Footer>
                <ScheduleSubmit />
              </Card.Footer>
            </ScheduleAvailableTimeSlotCard>
          </div>
        </ScheduleContextProvider>
      )}
    </>
  );
};
